import { useState } from "react";

import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";

import { useBusinessUnitNames } from "./use-business-unit-names";

/**
 * Radix refuses an empty item value, and any readable sentinel could collide
 * with a real unit name, so this one is deliberately unspellable.
 */
const OTHER = "__other__";

/**
 * Company: one of the business units, or anything else typed out.
 *
 * The same control serves the admin attendee form and a person's own profile,
 * over the one vocabulary in `business_units`.
 *
 * Two of its states exist to avoid losing a value somebody already has:
 *
 *  - A stored company that is not in the vocabulary — which is every record
 *    written before there was one — opens under Other with that text in the
 *    box, so saving an unrelated field writes it back unchanged.
 *  - An empty or unreadable vocabulary leaves the free-text box on its own. A
 *    dropdown holding nothing but Other is a worse version of the text field
 *    it replaced.
 */
export function CompanyField({
  id,
  testId,
  value,
  onChange,
}: {
  id: string;
  testId: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const { names, loading } = useBusinessUnitNames();
  // Only needed for the gap between choosing Other and typing anything: the
  // value is empty then, and nothing about it says which half of the control
  // the person is looking at.
  const [otherChosen, setOtherChosen] = useState(false);

  const freeText = (
    <Input
      id={id}
      testId={testId}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );

  if (loading || names.length === 0) return freeText;

  const listed = value !== "" && names.includes(value);
  const showOther = !listed && (otherChosen || value !== "");

  return (
    <div className="space-y-1.5">
      <Select
        value={showOther ? OTHER : value}
        onValueChange={(next) => {
          setOtherChosen(next === OTHER);
          onChange(next === OTHER ? "" : next);
        }}
      >
        <SelectTrigger className="w-full" testId={`${testId}-select`}>
          <SelectValue placeholder="Choose a company" />
        </SelectTrigger>
        <SelectContent>
          {names.map((name) => (
            <SelectItem key={name} value={name}>
              {name}
            </SelectItem>
          ))}
          <SelectItem value={OTHER}>Other</SelectItem>
        </SelectContent>
      </Select>
      {showOther && freeText}
    </div>
  );
}
